import React, { useEffect, useState } from "react";
import FileUpload from "./FileUpload";
import PageLayout from "@/react/features/styled/PageLayout";
import InlineBlock from "@/react/features/styled/InlineBlock";
import { StarIcon } from "@8x8/oxygen-icon";
import { Button } from "@/react/styled";
import GeneratedTextArea from "./GeneratedTextArea";
import Skeleton from "./Skeleton";
import { COLORS } from "@/react/theme/constants";
import useTranscript from "./useTranscript";
import useSummary from "./useSummary";
import { RefreshIcon } from "@8x8/oxygen-icon/icons";
import { ConfirmMessage } from "@/react/components/ConfirmMessage";
import styled from "styled-components";

const ROWS = 20;
const SKELETON_ROWS = 10;

const SkeletonWrapper = styled.div`
  width: 100%;
  margin-top: 16px;
`;

const VALID_MIME_TYPES = Object.freeze([
  "audio/mpeg",
  "audio/wav",
  "video/mp4",
]);
const INFO_MESSAGES = Object.freeze([
  "Convert audio/video files to text",
  "Drag and drop files here, or click to select files",
  "File extensions accepted: mp3, mp4, wav",
]);

const TranscribePage = () => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [askRedo, setAskRedo] = useState(false);
  const {
    transcript,
    isTranscriptLoading,
    setTranscript,
    sendTranscriptRequest,
  } = useTranscript();
  const { summary, isSummaryLoading, setSummary, sendSummaryRequest } =
    useSummary();

  const handleFileUpload = (files) => {
    if (files && files.length > 0) {
      setUploadedFile(files[0]);
    }
  };

  const handleRedo = () => {
    setAskRedo(true);
  };

  const handleRedoConfirmed = () => {
    setUploadedFile(null);
    setTranscript(null);
    setSummary(null);
    setAskRedo(false);
  };

  const handleRedoInfirmed = () => {
    setAskRedo(false);
  };

  useEffect(() => {
    uploadedFile && sendTranscriptRequest(uploadedFile, setUploadedFile);
  }, [uploadedFile]);

  const isUploaded = Boolean(uploadedFile);

  return (
    <PageLayout>
      {askRedo && (
        <ConfirmMessage
          type={"warning"}
          handleConfirm={handleRedoConfirmed}
          handleInfirm={handleRedoInfirmed}
          question={"You will lose the current transcript. Are you sure?"}
          title={"Upload another file"}
        />
      )}
      <FileUpload
        onFileUpload={handleFileUpload}
        isUploaded={isUploaded}
        validMimeTypes={VALID_MIME_TYPES}
        infoMessages={INFO_MESSAGES}
      />
      {isUploaded && isTranscriptLoading && <Skeleton rows={SKELETON_ROWS} />}

      {transcript && (
        <>
          <GeneratedTextArea value={transcript} rows={ROWS} />
          {isSummaryLoading && (
            <SkeletonWrapper>
              <Skeleton rows={SKELETON_ROWS / 2} />
            </SkeletonWrapper>
          )}
          {summary && <GeneratedTextArea value={summary} rows={ROWS / 2} />}

          <InlineBlock marginTop={16}>
            {/*The "Summary" button should only be visible if the summary is not
            already generated to avoid unintentional API calls*/}
            {!summary && (
              <Button
                isDisabled={isSummaryLoading}
                marginRight={16}
                onClick={() => sendSummaryRequest(transcript)}
              >
                <InlineBlock>
                  <StarIcon color={COLORS.background} size={18} /> Summarize
                </InlineBlock>
              </Button>
            )}

            <Button onClick={handleRedo}>
              <InlineBlock>
                <RefreshIcon color={COLORS.background} size={18} /> Upload
                another file
              </InlineBlock>
            </Button>
          </InlineBlock>
        </>
      )}
    </PageLayout>
  );
};

export default TranscribePage;
